import { useAuth } from "@/auth/auth";
import { useSessionStorage } from "@/auth/storage.tsx";
import { useUser } from "@/client/backend/user.tsx";
import type { components } from "@/client/backend/zz_schema";
import { useCallback, useEffect, useMemo } from "react";
import useSWRMutation from "swr/mutation";
import useSWR from "swr";

export function useUpdateOrganization(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}` : null,
    (_, { arg }: { arg: components["schemas"]["OrganizationMutable"] }) => {
      if (organizationId === "loading") {
        console.log("cannot update organization while it is still loading");
        return undefined;
      }
      return auth.client.PATCH("/organizations/{organizationId}", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function useOIDCConnection(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWR(
    organizationId !== "loading" ? `/organizations/${organizationId}/oidcConnection` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/oidcConnection", {
        params: {
          path: {
            organizationId,
          },
        },
      });
    },
  );
}

export function useOrganizationDomains(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWR(
    organizationId !== "loading" ? `/organizations/${organizationId}/domains` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/domains", {
        params: {
          path: {
            organizationId,
          },
        },
      });
    },
  );
}

export function usePutOrganizationDomains(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/domains` : null,
    (_, { arg }: { arg: components["schemas"]["OrganizationDomainsMutable"] }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.PUT("/organizations/{organizationId}/domains", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function usePutOIDCConnection(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/oidcConnection` : null,
    (_, { arg }: { arg: components["schemas"]["OIDCConnectionMutable"] }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.PUT("/organizations/{organizationId}/oidcConnection", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function useDeleteOIDCConnection(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/oidcConnection` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.DELETE("/organizations/{organizationId}/oidcConnection", {
        params: {
          path: {
            organizationId,
          },
        },
      });
    },
  );
}

export function useVerifyOrganizationDomain(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/domainVerification` : null,
    (_, { arg }: { arg: { domain: string } }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.POST("/organizations/{organizationId}/domains/{domain}/verify", {
        params: {
          path: {
            organizationId,
            domain: arg.domain,
          },
        },
      });
    },
  );
}

export function useCreateOrganization() {
  const auth = useAuth();

  return useSWRMutation(
    "/organizations",
    (_, { arg }: { arg: components["schemas"]["OrganizationCreate"] }) => {
      return auth.client.POST("/organizations", {
        body: arg,
      });
    },
  );
}

export function useOrganizationMembers(organizationId: string | "loading") {
  const auth = useAuth();

  // Keyed like the mutations that revalidate it after an invite, a role
  // change or a removal.
  return useSWR(
    organizationId !== "loading" ? `/organizations/${organizationId}/members` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/members", {
        params: {
          path: {
            organizationId,
          },
        },
      });
    },
  );
}

// PUT on a member seats a new person or moves an existing seat to the role.
export function useSetOrganizationMemberRole(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/members` : null,
    (_, { arg }: { arg: { emailAddress: string; role: components["schemas"]["Role"] } }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.PUT("/organizations/{organizationId}/members", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function useRemoveOrganizationMember(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/members` : null,
    (_, { arg }: { arg: { emailAddress: string } }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.DELETE("/organizations/{organizationId}/members", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function useSendSupportMessage(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/support` : null,
    (_, { arg }: { arg: components["schemas"]["SupportMessage"] }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.POST("/organizations/{organizationId}/support", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function useInviteUser(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/invitations` : null,
    (_, { arg }: { arg: components["schemas"]["InvitationRequest"] }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.POST("/organizations/{organizationId}/invitations", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

/** The organization's open invitations: seats offered that nobody has taken yet. */
export function useOrganizationInvitations(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWR(
    organizationId !== "loading" ? `/organizations/${organizationId}/invitations` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/invitations", {
        params: {
          path: {
            organizationId,
          },
        },
      });
    },
  );
}

export function useRevokeInvitation(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/invitations` : null,
    (_, { arg }: { arg: { invitationId: string } }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.DELETE("/organizations/{organizationId}/invitations/{invitationId}", {
        params: {
          path: {
            organizationId,
            invitationId: arg.invitationId,
          },
        },
      });
    },
  );
}

export function useAcceptInvitation() {
  const auth = useAuth();

  return useSWRMutation(
    `/organizations/{organizationId}/invitations/accept`,
    (_, { arg }: { arg: { organizationId: string } }) => {
      return auth.client.POST("/organizations/{organizationId}/invitations/accept", {
        params: {
          path: {
            organizationId: arg.organizationId,
          },
        },
      });
    },
  );
}

export function useDeclineInvitation() {
  const auth = useAuth();

  return useSWRMutation(
    `/organizations/{organizationId}/invitations/decline`,
    (_, { arg }: { arg: { organizationId: string } }) => {
      return auth.client.POST("/organizations/{organizationId}/invitations/decline", {
        params: {
          path: {
            organizationId: arg.organizationId,
          },
        },
      });
    },
  );
}

export function useGetBillingSession(organizationId: string | "loading", emailAddress?: string) {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/billing` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/billing", {
        params: {
          path: {
            organizationId,
          },
          query: emailAddress ? { emailAddress } : {},
        },
      });
    },
  );
}

export function useGetCheckoutSession(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/checkout` : null,
    (_, { arg }: { arg: { priceId: string } }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/checkout", {
        params: {
          path: {
            organizationId,
          },
          query: {
            priceId: arg.priceId,
          },
        },
      });
    },
  );
}

export function useSubscriptions(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWR(
    organizationId !== "loading" ? `/organizations/${organizationId}/subscriptions` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/subscriptions", {
        params: {
          path: {
            organizationId,
          },
        },
      });
    },
  );
}

export function useOrganizationBillingDetails(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWR(
    organizationId !== "loading" ? `/organizations/${organizationId}/billing-details` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/billing-details", {
        params: {
          path: {
            organizationId,
          },
        },
      });
    },
  );
}

export function useOrganizationInvoices(
  organizationId: string | "loading",
  options?: {
    from?: string;
    to?: string;
    timezone?: string;
  }
) {
  const auth = useAuth();

  return useSWR(
    organizationId !== "loading"
      ? `/organizations/${organizationId}/invoices?from=${options?.from || ""}&to=${options?.to || ""}&timezone=${options?.timezone || ""}`
      : null,
    async () => {
      if (organizationId === "loading") return undefined;
      const result = await auth.client.GET("/organizations/{organizationId}/invoices", {
        params: {
          path: {
            organizationId,
          },
          query: {
            includeUpcoming: true,
            from: options?.from,
            to: options?.to,
            timezone: options?.timezone,
          },
        },
      });
      if (result.error) {
        throw new Error(result.error.message || "Could not fetch invoices");
      }
      return result;
    },
    { refreshInterval: 60_000 },
  );
}

export function useOrganization(): [
  components["schemas"]["Organization"] | "loading",
  (organization: components["schemas"]["Organization"]) => void,
] {
  const [storedOrganization, setStoredOrganization] = useSessionStorage("organization");
  const [, setOrganizationTid] = useSessionStorage("organizationTid");
  const { user } = useUser();

  const setOrganization = useCallback(
    (organization: components["schemas"]["Organization"]) => {
      setOrganizationTid(organization.tid);
      setStoredOrganization(JSON.stringify(organization));
    },
    [setOrganizationTid, setStoredOrganization],
  );

  // Parse the stored organization once per change. Returning a freshly parsed
  // object on every render would give consumers a new reference each time, which
  // makes effects keyed on `organization` re-run forever (React error #185).
  const parsedStoredOrganization = useMemo(() => {
    if (!storedOrganization) return null;
    try {
      return JSON.parse(
        storedOrganization,
      ) as components["schemas"]["Organization"];
    } catch {
      return null;
    }
  }, [storedOrganization]);

  // Resolve the active organization to a stable reference.
  const organization = useMemo<
    components["schemas"]["Organization"] | "loading"
  >(() => {
    // Before user data resolves, fall back to the stored organization if present.
    if (!user || user === "loading") {
      return parsedStoredOrganization ?? "loading";
    }
    // Once user data is available, prefer the matching org from it so we always
    // render the current shape rather than a possibly stale stored copy.
    if (parsedStoredOrganization) {
      const matched = user.organizations.find(
        (o) => o.id === parsedStoredOrganization.id,
      );
      if (matched) return matched;
    }
    return user.defaultOrganization;
  }, [user, parsedStoredOrganization]);

  // Persist the default organization when we had to fall back to it. Done in an
  // effect (not during render) to avoid scheduling state updates while rendering.
  useEffect(() => {
    if (!user || user === "loading") return;
    const hasMatch =
      parsedStoredOrganization != null &&
      user.organizations.some((o) => o.id === parsedStoredOrganization.id);
    if (!hasMatch && user.defaultOrganization) {
      setOrganization(user.defaultOrganization);
    }
  }, [user, parsedStoredOrganization, setOrganization]);

  return [organization, setOrganization];
}

export function usePlanOptions(organizationId: string | "loading") {
  const auth = useAuth();
  const result = useSWR(
    organizationId !== "loading" ? `/organizations/${organizationId}/plans` : null,
    () => organizationId === "loading" ? undefined : auth.client.GET("/organizations/{organizationId}/plans", { params: { path: { organizationId } } }),
  );
  return {
    options: result.data?.data ?? [],
    mutate: result.mutate,
    error: result.error ?? result.data?.error,
    isLoading: result.isLoading,
  };
}

// Returns the Stripe page where the customer reviews and confirms the plan change.
export function useChangeSubscriptionPlan(organizationId: string | "loading") {
  const auth = useAuth();
  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/plan/change` : null,
    (_, { arg }: { arg: { priceId: string } }) => organizationId === "loading" ? undefined : auth.client.POST("/organizations/{organizationId}/plan", { params: { path: { organizationId } }, body: arg }),
  );
}

export function useScheduledPlanChange(organizationId: string | "loading") {
  const auth = useAuth();
  return useSWR(
    organizationId !== "loading" ? `/organizations/${organizationId}/plan/schedule` : null,
    () => auth.client.GET("/organizations/{organizationId}/plan/schedule", {
      params: { path: { organizationId } },
    }),
  );
}

export function useCancelScheduledPlanChange(organizationId: string | "loading") {
  const auth = useAuth();
  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/plan/schedule` : null,
    () => auth.client.DELETE("/organizations/{organizationId}/plan/schedule", {
      params: { path: { organizationId } },
    }),
  );
}
